import React, { useState, useEffect } from 'react';
import Button from '../../../../components/Button';
import Select from '../../../../components/Select';
import ModuleSection from '../ModuleSection';
import blogStorage from '../../utils/storage';
import './index.scss';

const CategoryManager = () => {
  const [categories, setCategories] = useState([]);
  const [isCreating, setIsCreating] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentId: null
  });

  // 加载分类数据
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = () => {
    const data = blogStorage.getCategories();
    setCategories(data);
  };

  // 处理表单输入
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 创建分类
  const handleCreate = () => {
    setIsCreating(true);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      parentId: null
    });
  };

  // 编辑分类
  const handleEdit = (category) => {
    setEditingCategory(category);
    setIsCreating(false);
    setFormData({
      name: category.name,
      description: category.description,
      parentId: category.parentId
    });
  };

  // 保存分类
  const handleSave = () => {
    if (!formData.name.trim()) {
      alert('请输入分类名称');
      return;
    }

    try {
      if (editingCategory) {
        // 编辑模式
        const updated = blogStorage.updateCategory(editingCategory.id, formData);
        if (updated) {
          loadCategories();
          handleCancel();
        }
      } else {
        // 创建模式
        const newCategory = blogStorage.addCategory(formData);
        if (newCategory) {
          loadCategories();
          handleCancel();
        }
      }
    } catch (error) {
      console.error('保存分类失败:', error);
      alert('保存失败，请重试');
    }
  };

  // 取消编辑
  const handleCancel = () => {
    setIsCreating(false);
    setEditingCategory(null);
    setFormData({
      name: '',
      description: '',
      parentId: null
    });
  };

  // 删除分类
  const handleDelete = (category) => {
    if (category.id === 'cat_default') {
      alert('默认分类不能删除');
      return;
    }

    if (category.articleCount > 0) {
      if (!confirm(`分类"${category.name}"中有${category.articleCount}篇文章，删除后文章将移动到默认分类。确定要删除吗？`)) {
        return;
      }
    } else {
      if (!confirm(`确定要删除分类"${category.name}"吗？`)) {
        return;
      }
    }

    try {
      const success = blogStorage.deleteCategory(category.id);
      if (success) {
        loadCategories();
      }
    } catch (error) {
      console.error('删除分类失败:', error);
      alert('删除失败，请重试');
    }
  };

  // 获取父分类选项
  const getParentOptions = () => {
    return categories.filter(cat => 
      cat.parentId === null && 
      (!editingCategory || cat.id !== editingCategory.id)
    );
  };

  // 获取子分类
  const getChildCategories = (parentId) => {
    return categories.filter(cat => cat.parentId === parentId);
  };

  // 获取顶级分类
  const getTopLevelCategories = () => {
    return categories.filter(cat => cat.parentId === null);
  };

  return (
    <div className="category-manager">
      {/* 创建/编辑表单 */}
      {(isCreating || editingCategory) && (
        <ModuleSection
          title={editingCategory ? '编辑分类' : '创建分类'}
          variant="info"
        >
          
          <div className="form-group">
            <label>分类名称 *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="请输入分类名称"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>分类描述</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="请输入分类描述（可选）"
              className="form-textarea"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>父分类</label>
            <Select
              value={formData.parentId || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, parentId: value }))}
              options={[
                { value: '', label: '顶级分类' },
                ...getParentOptions().map(category => ({
                  value: category.id,
                  label: category.name
                }))
              ]}
              placeholder="请选择父分类"
              dropdownPortal={true}
            />
          </div>

          <div className="form-actions">
            <Button 
              onClick={handleCancel}
            >
              取消
            </Button>
            <Button 
              type="primary"
              onClick={handleSave}
            >
              保存
            </Button>
          </div>
        </ModuleSection>
      )}

      {/* 分类列表 */}
      <ModuleSection
        title="分类管理"
        headerExtra={`共 ${categories.length} 个分类`}
        actions={
          <Button 
            type="primary"
            onClick={handleCreate}
          >
            + 创建分类
          </Button>
        }
        variant="list"
        noPadding
      >
        <div className="category-list">
        {getTopLevelCategories().map(category => (
          <div key={category.id} className="category-group">
            <div className="category-card">
              <div className="category-info">
                <div className="category-main">
                  <h4>{category.name}</h4>
                  <p className="category-desc">{category.description || '暂无描述'}</p>
                </div>
                <div className="category-stats">
                  <span className="article-count">{category.articleCount} 篇文章</span>
                  <span className="create-time">
                    创建于 {new Date(category.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
              
              <div className="category-actions">
                <Button 
                  size="small"
                  onClick={() => handleEdit(category)}
                >
                  编辑
                </Button>
                {category.id !== 'cat_default' && (
                  <Button 
                    size="small"
                    type="danger"
                    onClick={() => handleDelete(category)}
                  >
                    删除
                  </Button>
                )}
              </div>
            </div>

            {/* 子分类 */}
            {getChildCategories(category.id).map(childCategory => (
              <div key={childCategory.id} className="category-card child-category">
                <div className="category-info">
                  <div className="category-main">
                    <h5>└ {childCategory.name}</h5>
                    <p className="category-desc">{childCategory.description || '暂无描述'}</p>
                  </div>
                  <div className="category-stats">
                    <span className="article-count">{childCategory.articleCount} 篇文章</span>
                    <span className="create-time">
                      创建于 {new Date(childCategory.createdAt).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="category-actions">
                  <Button 
                    size="small"
                    onClick={() => handleEdit(childCategory)}
                  >
                    编辑
                  </Button>
                  <Button 
                    size="small"
                    type="danger"
                    onClick={() => handleDelete(childCategory)}
                  >
                    删除
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ))}

          {categories.length === 0 && (
            <div className="empty-state">
              <div className="empty-icon">📁</div>
              <p>暂无分类，点击"创建分类"开始吧！</p>
            </div>
          )}
        </div>
      </ModuleSection>
    </div>
  );
};

export default CategoryManager;
